/**
 * Pane-system pure helpers (W7-27) — ported from the frozen concept design
 * (`concept/src/App.tsx` shell state + `concept/src/components/layout/Pane.tsx`
 * drop/split logic + `concept/src/components/panes/*`).
 *
 * Everything here is UI-framework-free so it runs under `bun` probes.
 * Rendering lives in `pane.tsx` / `split-tree.tsx` / `windowed-canvas.tsx` /
 * `tab-icons.tsx` / `workspace.tsx`. No mock content is ever produced here —
 * every factory carries a real session id, inspector id, or file path.
 */
import type { LayoutNode } from '@/stores/layout';
import type { InspectorTab } from '@/components/providers';

/** Drag MIME type for session rows (same wire as the sessions sidebar). */
export const SESSION_DRAG_MIME = 'application/x-lokma-session';

/**
 * Drag MIME type for rail icons (REQ-026: InspectorRail + ActivityBar) —
 * every app surface becomes a pane tab through the same drop flow as
 * sessions and files.
 */
export const INSPECTOR_DRAG_MIME = 'application/x-lokma-inspector';

/** Tab-bar drag payload MIME (a whole real tab moving between panes). */
export const PANE_TAB_MIME = 'application/x-lokma-tab';

/**
 * Window event for the tiling reset button (REQ-045: the TilingBar strip is
 * gone, its Reset lived in the workspace). AppShell dispatches, the
 * workspace listens — same CustomEvent pattern as FOCUS_FILES_EVENT.
 */
export const RESET_LAYOUT_EVENT = 'lokma:reset-layout';

/** localStorage key for the per-pane tab snapshot (layout tree itself lives in `lokma:layout:v1`). */
export const TILING_TABS_KEY = 'lokma:tiling-tabs:v1';

/** Every Inspector tab the tiling bar can open (single source — mirrors the right Inspector). */
export const INSPECTOR_TABS = [
  // REQ-043 — Files is its own Inspector page (VS Code Explorer position,
  // first), no longer docked above every other tab.
  { id: 'files', label: 'Files' },
  { id: 'providers', label: 'Providers' },
  { id: 'models', label: 'Models' },
  { id: 'usage', label: 'Usage' },
  { id: 'settings', label: 'Settings' },
  { id: 'terminal', label: 'Terminal' },
  { id: 'git', label: 'Git' },
  { id: 'browser', label: 'Browser' },
  // REQ-163/164/165/166 — Agent Hub, Orchestration, Vault, Memory and
  // Skills are no longer pane/tabs: they open as Settings modal sections
  // (rail click), so the pane registry carries none of them.
  // REQ-167 — Archify is not a pane/tab either: its rail icon opens its own
  // standalone modal, so the registry carries no 'archify' entry.
  // REQ-168 — Design is the third top-level MODE (its own full page behind
  // the header switch, like Bots): no pane entry, no rail entry.
  { id: 'testing', label: 'Testing' },
  { id: 'auth', label: 'Auth' },
  { id: 'setup', label: 'Setup' },
  { id: 'plugins', label: 'Plugins' },
  { id: 'observability', label: 'Observability' },
  { id: 'cron', label: 'Cron' },
  // REQ-202 — the Loops console: the rail entry's pane tab (kapsam 1).
  // Same component as Settings → Loops, one implementation.
  { id: 'loops', label: 'Loops' },
  // REQ-204 — the Office pane (slice 2): the rail entry's tab — same pane
  // route every other pane surface takes.
  { id: 'office', label: 'Office' },
  { id: 'extras', label: 'Extras' },
  { id: 'todos', label: 'Todos' },
] as const;

export type InspectorTabId = (typeof INSPECTOR_TABS)[number]['id'];

/** The concept TilingBar entries (concept prop names kept as comments). */
export const TILING_BAR_TABS: InspectorTabId[] = [
  'terminal', // onOpenTerminal
  'git', // onOpenGit
  'usage', // onOpenUsage
  'settings', // onOpenSettings
  'testing', // onOpenTesting
  'auth', // onOpenAuth
  'setup', // onOpenSetup
  'plugins', // onOpenPlugins
  'observability', // onOpenObservability
  'cron', // onOpenCron
  'loops', // onOpenLoops (REQ-202)
  'office', // onOpenOffice (REQ-204)
  'extras', // onOpenExtras
  'browser', // harness addition (W3-12 per-agent tabs)
];

export function isInspectorTabId(value: unknown): value is InspectorTabId {
  return typeof value === 'string' && (INSPECTOR_TABS as readonly { id: string }[]).some((t) => t.id === value);
}

/**
 * REQ-109 — sidebar surfaces that always open as a tiling pane tab, never
 * in a sidebar. The rail/activity icons stay visible (and their drags keep
 * working — drops already land in panes), but a CLICK routes to
 * `requestInspectorTab` instead of the sidebar Inspector. The pane registry
 * above keeps the id so drops, the tab picker and `InspectorHost` resolve.
 */
export const PANE_ONLY_TABS = ['browser'] as const;

export type PaneOnlyTab = (typeof PANE_ONLY_TABS)[number];

export function isPaneOnlyTab(value: unknown): value is PaneOnlyTab {
  return typeof value === 'string' && (PANE_ONLY_TABS as readonly string[]).includes(value);
}

export function inspectorLabel(id: InspectorTabId): string {
  return INSPECTOR_TABS.find((t) => t.id === id)?.label ?? id;
}

/** What a tiling tab points at — always a live surface, never mock content. */
export type PaneTabKind = 'session' | 'inspector' | 'file';

export type PaneTab = {
  id: string;
  title: string;
  kind: PaneTabKind;
  /** Live session id (session tabs render a real Chat; file tabs resolve cwd from this session). */
  sessionId?: string;
  /** Inspector tab id (inspector tabs render the real pane). */
  inspectorId?: InspectorTabId;
  /** Workspace-relative file path (file tabs render a real read preview). */
  filePath?: string;
};

export type PaneTabState = { tabs: PaneTab[]; active: string | null };

let tabSeq = 0;

/** Unique tab id (prefix keeps tab-bar drag payloads recognizable). */
export function makeTabId(prefix = 'tab'): string {
  tabSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${tabSeq.toString(36)}`;
}

/** Unique pane id (same shape as the concept `genId`). */
export function makePaneId(): string {
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
}

export function makeSessionTab(sessionId: string, title?: string): PaneTab {
  const short = sessionId.length > 24 ? `${sessionId.slice(0, 24)}…` : sessionId;
  return { id: makeTabId('tab-session'), title: title ?? short, kind: 'session', sessionId };
}

export function makeInspectorTab(inspectorId: InspectorTabId): PaneTab {
  return {
    id: makeTabId('tab-tool'),
    title: inspectorLabel(inspectorId),
    kind: 'inspector',
    inspectorId,
  };
}

export function makeFileTab(filePath: string, sessionId: string): PaneTab {
  const base = filePath.split('/').pop() || filePath;
  return { id: makeTabId('tab-file'), title: base, kind: 'file', filePath, sessionId };
}

/** Preview family by extension (REQ-075) — pure so probes cover it. */
export type FilePreviewKind = 'markdown' | 'html' | 'pdf' | 'image' | 'text';
export function filePreviewKind(path: string): FilePreviewKind {
  const ext = path.toLowerCase().split('.').pop() ?? '';
  if (ext === 'md' || ext === 'markdown') return 'markdown';
  if (ext === 'html' || ext === 'htm') return 'html';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'gif' || ext === 'webp' || ext === 'svg') return 'image';
  return 'text';
}

/**
 * Does this page need scripts to become what its author meant? (REQ-137) The
 * html preview runs sandboxed without script permission — safe, but a
 * self-contained demo (WebGL scene, loader-driven UI) then just sits on its
 * loading screen and reads as broken. Detecting the tag lets the pane explain
 * why and offer the opt-in instead of looking dead.
 */
export function htmlNeedsScripts(html: string): boolean {
  return /<script[\s>]/i.test(html);
}

/**
 * Open-or-focus a file tab in a pane state (REQ-002): the same path owned by
 * the same session focuses its existing tab instead of duplicating it;
 * anything else appends a fresh live file tab and activates it.
 */
export function upsertFileTab(state: PaneTabState, filePath: string, sessionId: string): PaneTabState {
  const existing = state.tabs.find((t) => t.kind === 'file' && t.filePath === filePath && t.sessionId === sessionId);
  if (existing) return { tabs: state.tabs, active: existing.id };
  const tab = makeFileTab(filePath, sessionId);
  return { tabs: [...state.tabs, tab], active: tab.id };
}

/**
 * Open-or-focus a session tab in a pane state (REQ-005): the same session
 * focuses its existing tab instead of duplicating it; anything else appends
 * a fresh live session tab and activates it.
 */
export function upsertSessionTab(state: PaneTabState, sessionId: string, title?: string): PaneTabState {
  const existing = state.tabs.find((t) => t.kind === 'session' && t.sessionId === sessionId);
  if (existing) return { tabs: state.tabs, active: existing.id };
  const tab = makeSessionTab(sessionId, title);
  return { tabs: [...state.tabs, tab], active: tab.id };
}

/**
 * Open-or-focus an inspector tab in a pane state (REQ-057): the same
 * inspector focuses its existing tab instead of duplicating it; anything
 * else appends a fresh inspector tab and activates it.
 */
export function upsertInspectorTab(state: PaneTabState, inspectorId: InspectorTabId): PaneTabState {
  const existing = state.tabs.find((t) => t.kind === 'inspector' && t.inspectorId === inspectorId);
  if (existing) return { tabs: state.tabs, active: existing.id };
  const tab = makeInspectorTab(inspectorId);
  return { tabs: [...state.tabs, tab], active: tab.id };
}

/** Runtime guard for persisted / drag-carried tabs (corrupt rows are dropped, never rendered). */
export function isPaneTab(value: unknown): value is PaneTab {
  if (typeof value !== 'object' || value === null) return false;
  const t = value as Record<string, unknown>;
  if (typeof t.id !== 'string' || typeof t.title !== 'string') return false;
  if (t.kind === 'session') return typeof t.sessionId === 'string' && t.sessionId.length > 0;
  if (t.kind === 'inspector') return isInspectorTabId(t.inspectorId);
  if (t.kind === 'file') return typeof t.filePath === 'string' && typeof t.sessionId === 'string';
  return false;
}

/** Session ids are server-minted slugs (`sess_*`, agent ids, …) — never paths. */
export function isValidSessionId(id: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(id);
}

/** Workspace-relative file path (mirrors the server `resolveInRoot` jail, loosely). */
export function isValidRelPath(path: string): boolean {
  if (!path || path.length > 512 || path.startsWith('/') || path.includes('\\')) return false;
  return !path.split('/').some((seg) => seg === '' || seg === '.' || seg === '..');
}

type DataGetter = Pick<DataTransfer, 'getData'>;

/** Real session id carried by a sidebar-row drag (MIME first, text fallback). */
export function parseSessionDrop(dt: DataGetter): string | null {
  const viaMime = dt.getData(SESSION_DRAG_MIME).trim();
  if (viaMime && isValidSessionId(viaMime)) return viaMime;
  // Sidebar rows also set text/plain to the display title — never a valid id,
  // so a text-only drop is not openable (caller toasts instead of faking a tab).
  return null;
}

/**
 * What a rail-icon drag carries (REQ-026): any Inspector tab id, plus the
 * literal 'sessions' for the sessions list — which lives in the Explorer
 * sidebar, not the Inspector, so its drop opens the pane tab picker
 * (the live sessions + tools chooser) instead of a tool tab.
 */
export type RailDropId = InspectorTab | 'sessions';

export function isRailDropId(value: unknown): value is RailDropId {
  return value === 'sessions' || isInspectorTabId(value);
}

/** Encode a rail-icon drag payload (plain id string — validated on drop). */
export function encodeInspectorDrag(id: RailDropId): string {
  return id;
}

/**
 * Real rail surface carried by a rail-icon drag (MIME only, never the
 * text/plain label — labels like "Terminal" would otherwise parse as file
 * paths downstream, so the caller must run this BEFORE parseFileDrop).
 */
export function parseInspectorDrop(dt: DataGetter): RailDropId | null {
  const raw = dt.getData(INSPECTOR_DRAG_MIME).trim();
  if (raw && isRailDropId(raw)) return raw;
  return null;
}

/** Real file path carried by a file-row drag (`@path` text fallback included). */
export function parseFileDrop(dt: DataGetter, fileMime: string): string | null {
  const viaMime = dt.getData(fileMime).trim();
  if (viaMime && isValidRelPath(viaMime)) return viaMime;
  const text = dt.getData('text/plain').trim();
  const atPath = text.startsWith('@') ? text.slice(1).trim() : text;
  if (atPath && isValidRelPath(atPath)) return atPath;
  return null;
}

/** Decode a tab-bar drag payload (a whole real tab moving panes). */
export function parseTabMove(dt: DataGetter): { fromPane: string; tab: PaneTab } | null {
  const raw = dt.getData(PANE_TAB_MIME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { fromPane?: unknown; tab?: unknown };
    if (typeof parsed.fromPane !== 'string' || !isPaneTab(parsed.tab)) return null;
    return { fromPane: parsed.fromPane, tab: parsed.tab };
  } catch {
    return null;
  }
}

export function encodeTabMove(fromPane: string, tab: PaneTab): string {
  return JSON.stringify({ fromPane, tab });
}

export type DropZone = 'center' | 'left' | 'right' | 'top' | 'bottom';

/** Windows-snap 5-zone computation (same 24% edge rule as the concept Pane). */
export function dropZoneFor(w: number, h: number, x: number, y: number, edge = 0.24): DropZone {
  if (x < w * edge) return 'left';
  if (x > w * (1 - edge)) return 'right';
  if (y < h * edge) return 'top';
  if (y > h * (1 - edge)) return 'bottom';
  return 'center';
}

/** Split direction + position implied by an edge drop zone. */
export function splitForZone(zone: DropZone): { dir: 'row' | 'col'; pos: 'before' | 'after' } | null {
  if (zone === 'left') return { dir: 'row', pos: 'before' };
  if (zone === 'right') return { dir: 'row', pos: 'after' };
  if (zone === 'top') return { dir: 'col', pos: 'before' };
  if (zone === 'bottom') return { dir: 'col', pos: 'after' };
  return null;
}

/**
 * Compatible dropEffect for a drag source's effectAllowed (REQ-029).
 * Chrome enforces the pair: a drop whose dropEffect is incompatible with
 * the source's effectAllowed is silently rejected (no drop event fires).
 * Session/file rows drag with 'copy', rails and tab strips with 'move' —
 * answering every dragover with a hardcoded 'move' killed all session and
 * file drops. Answer with an effect the source allows instead.
 */
export function dropEffectFor(effectAllowed: string): 'copy' | 'move' | 'link' {
  if (effectAllowed === 'link') return 'link';
  if (
    effectAllowed === 'move' ||
    effectAllowed === 'copyMove' ||
    effectAllowed === 'linkMove' ||
    effectAllowed === 'all' ||
    effectAllowed === 'uninitialized' ||
    effectAllowed === ''
  ) {
    return 'move';
  }
  return 'copy';
}

// ─── Layout-tree ops (pure mirrors of the concept App split/close/resize) ────

let splitSeq = 0;

/** Insert a fresh pane next to `targetId` (concept `splitPane` recur). */
export function splitLayout(
  layout: LayoutNode,
  targetId: string,
  dir: 'row' | 'col',
  pos: 'before' | 'after',
  newPaneId: string,
): LayoutNode {
  const newPane: LayoutNode = { type: 'pane', id: newPaneId };
  const recur = (node: LayoutNode): LayoutNode => {
    if (node.type === 'pane') {
      if (node.id !== targetId) return node;
      const children = pos === 'before' ? [newPane, node] : [node, newPane];
      splitSeq += 1;
      return { type: 'split', id: `s-${Date.now().toString(36)}-${splitSeq}`, dir, sizes: [50, 50], children };
    }
    return { ...node, children: node.children.map(recur) };
  };
  return recur(layout);
}

/**
 * Remove a pane (concept `closePane` recur). Returns null when the tree would
 * go empty — the caller opens a fresh pane instead (last pane never vanishes).
 */
export function closeLayoutPane(layout: LayoutNode, targetId: string): LayoutNode | null {
  const recur = (node: LayoutNode): LayoutNode | null => {
    if (node.type === 'pane') return node.id === targetId ? null : node;
    const children = node.children.map(recur).filter((c): c is LayoutNode => c !== null);
    if (children.length === 0) return null;
    if (children.length === 1) return children[0];
    const sizes = node.sizes.slice(0, children.length);
    while (sizes.length < children.length) sizes.push(100 / children.length);
    const sum = sizes.reduce((a, b) => a + b, 0) || 1;
    return { ...node, children, sizes: sizes.map((s) => (s / sum) * 100) };
  };
  return recur(layout);
}

/** Resize a split node (concept SplitTree drag math, clamped 15–85). */
export function resizeLayoutNode(layout: LayoutNode, nodeId: string, sizes: number[]): LayoutNode {
  const update = (node: LayoutNode): LayoutNode => {
    if (node.type === 'pane') return node;
    if (node.id === nodeId) return { ...node, sizes };
    return { ...node, children: node.children.map(update) };
  };
  return update(layout);
}

/** Append a pane id as a new right-most column (concept `handleOpenTab` grow). */
export function appendLayoutPane(layout: LayoutNode, newPaneId: string): LayoutNode {
  const newPane: LayoutNode = { type: 'pane', id: newPaneId };
  if (layout.type === 'split') {
    const sizes = [...layout.sizes, 50];
    const sum = sizes.reduce((a, b) => a + b, 0) || 1;
    return { ...layout, children: [...layout.children, newPane], sizes: sizes.map((s) => (s / sum) * 100) };
  }
  return { type: 'split', id: 'root', dir: 'row', sizes: [50, 50], children: [layout, newPane] };
}

export function collectPaneIds(layout: LayoutNode): string[] {
  if (layout.type === 'pane') return [layout.id];
  return layout.children.flatMap(collectPaneIds);
}

// ─── Side-dock planning (REQ-145) ───────────────────────────────────────────

/** Pane currently holding this inspector tab, if any (REQ-145 dock reuse). */
export function paneWithInspector(
  states: Record<string, PaneTabState>,
  paneIds: string[],
  inspectorId: InspectorTabId,
): string | null {
  for (const pid of paneIds) {
    const tabs = states[pid]?.tabs ?? [];
    if (tabs.some((t) => t.kind === 'inspector' && t.inspectorId === inspectorId)) return pid;
  }
  return null;
}

/** What an "open to the side" request does to the current layout (REQ-145). */
export type SideDockPlan =
  | { kind: 'focus'; paneId: string } // already open — focus it, never duplicate
  | { kind: 'split'; paneId: string } // single-pane workspace — split beside it
  | { kind: 'dock'; paneId: string }; // already split — land in the right-most pane

/**
 * Plan a side-dock (REQ-145): an inspector that is already open is focused;
 * a single-pane workspace splits once and docks the fresh pane on the right;
 * a workspace with several panes keeps its layout and docks into the
 * right-most pane — the pane is never split a second time.
 */
export function planSideDock(
  states: Record<string, PaneTabState>,
  paneIds: string[],
  focusedPaneId: string,
  inspectorId: InspectorTabId,
): SideDockPlan | null {
  if (paneIds.length === 0) return null;
  const owner = paneWithInspector(states, paneIds, inspectorId);
  if (owner) return { kind: 'focus', paneId: owner };
  const target = paneIds.includes(focusedPaneId) ? focusedPaneId : paneIds[0];
  if (paneIds.length === 1) return { kind: 'split', paneId: target };
  return { kind: 'dock', paneId: paneIds[paneIds.length - 1] };
}

// ─── Fullscreen modal subtree ops (REQ-089) ────
//
// The fullscreen modal is a LIVE VIEW of one layout subtree, never a copy:
// the modal resolves `rootId` against the current tree on every render, the
// background renders a placeholder for panes under that root (geometry stays
// put, nothing mounts twice), and splits inside the modal widen the root to
// the fresh parent so the new sibling appears inside the modal. Closing the
// modal changes nothing — the live layout was underneath all along.

/** Find any node (pane or split) by id; null when collapsed/closed away. */
export function findLayoutNode(layout: LayoutNode, id: string): LayoutNode | null {
  if (layout.id === id) return layout;
  if (layout.type === 'split') {
    for (const child of layout.children) {
      const found = findLayoutNode(child, id);
      if (found) return found;
    }
  }
  return null;
}

/** Parent split of a pane; null for a root-level pane or an unknown id. */
export function findParentNode(layout: LayoutNode, paneId: string): LayoutNode | null {
  if (layout.type === 'pane') return null;
  for (const child of layout.children) {
    if (child.type === 'pane' && child.id === paneId) return layout;
    const deeper = findParentNode(child, paneId);
    if (deeper) return deeper;
  }
  return null;
}

/** True when `paneId` lives inside the subtree rooted at `rootId`. */
export function isPaneUnder(layout: LayoutNode, rootId: string, paneId: string): boolean {
  if (rootId === paneId) return true;
  const root = findLayoutNode(layout, rootId);
  if (!root) return false;
  return collectPaneIds(root).includes(paneId);
}

export function countPanes(layout: LayoutNode): number {
  return collectPaneIds(layout).length;
}

/** Serialize the per-pane tab snapshot (validated on load — corrupt rows drop). */
export function serializeTabStates(states: Record<string, PaneTabState>): string {
  return JSON.stringify(states);
}

export function parseTabStates(raw: string | null): Record<string, PaneTabState> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Record<string, PaneTabState> = {};
    for (const [paneId, state] of Object.entries(parsed)) {
      if (typeof state !== 'object' || state === null) continue;
      const { tabs, active } = state as { tabs?: unknown; active?: unknown };
      if (!Array.isArray(tabs)) continue;
      const clean = tabs.filter(isPaneTab);
      if (clean.length === 0) continue;
      out[paneId] = {
        tabs: clean,
        active: typeof active === 'string' && clean.some((t) => t.id === active) ? active : clean[0].id,
      };
    }
    return out;
  } catch {
    return {};
  }
}
